import { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '@/lib/firebase';
import type {
  Job,
  JobSummary,
  AnalysisMode,
  AnalysisOutput,
  TranscriptSegment,
  TopSegment,
  ClaimToVerify,
  ClaimVerification,
  RiskFlag,
  EpisodeMetadata,
  JobStatus,
  VisualContext,
} from '@/types';

const API_BASE = '/api';

/**
 * Returns headers including a Firebase ID token for the currently signed-in
 * user. Falls back gracefully to no Authorization header if no user is signed
 * in (e.g. during local development without auth).
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  try {
    const token = await user.getIdToken();
    return { Authorization: `Bearer ${token}` };
  } catch {
    return {};
  }
}

// --- API response types (snake_case from backend) ---

interface ApiJobStatusResponse {
  job_id: string;
  status: string;
  stages?: { name: string; status: string; error: string | null }[];
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  error: string | null;
  acquisition_method: string | null;
  analysis_scope: string | null;
  progress_pct: number | null;
  progress_detail: string | null;
}

interface ApiResultsResponse {
  job_id: string;
  status: string;
  mode: string;
  input_url: string;
  created_at: string;
  updated_at: string;
  acquisition_method: string | null;
  analysis_scope: string | null;
  metadata: {
    source_type: string;
    source_url: string;
    show_title: string;
    episode_title: string;
    description: string;
    publish_datetime: string | null;
    duration_seconds: number | null;
    language: string;
    thumbnail_url: string | null;
    author_name: string | null;
  } | null;
  transcript_segments: {
    segment_id: number;
    start_sec: number;
    end_sec: number;
    text: string;
    speaker: string | null;
    source: string;
  }[];
  visual_context: {
    has_video: boolean;
    speakers: { label: string; bio: string; role: string | null; image_url: string | null; first_appearance_sec: number }[];
    keyframes: { timestamp_sec: number; description: string; speakers_visible: string[]; thumbnail_url: string | null }[];
  } | null;
  // Signed URL to the exact transcoded audio the backend transcribed (null for YouTube / failures).
  playback_url: string | null;

  analysis: {
    newsworthiness_score: number;
    score_confidence: number;
    recommendation: string;
    rubric_scores: Record<string, number>;
    why_summary: string;
    categories: string[];
    timeliness_class: string;
    top_segments: {
      segment_ids: number[];
      start_sec: number;
      end_sec: number;
      summary: string;
      relevance_score: number;
      topics: string[];
    }[];
    claims_to_verify: {
      claim_text: string;
      timestamp_start_sec: number;
      timestamp_end_sec: number;
      segment_ids: number[];
      priority: string;
      suggested_sources: string[];
      verification: {
        verdict: string;
        confidence: number;
        evidence: string;
        reasoning: string;
        source_references: string[];
        adversarial_notes: string;
        final_verdict: string;
      } | null;
    }[];
    risk_flags: {
      type: string;
      description: string;
      severity: string;
      timestamp_start_sec: number;
      timestamp_end_sec: number;
      segment_ids: number[];
    }[];
    editorial_angles: {
      headline: string;
      summary: string;
      type: string;
    }[];
    limitations: string[];
  } | null;
}

// --- Status mapping ---

const STATUS_MAP: Record<string, JobStatus> = {
  queued: 'pending',
  resolving: 'resolving_source',
  extracting_metadata: 'extracting_metadata',
  acquiring_transcript: 'finding_transcript',
  analyzing_segments: 'analyzing_segments',
  synthesizing: 'synthesizing',
  fact_checking: 'fact_checking',
  complete: 'complete',
  partial: 'failed',
  failed: 'failed',
};

function mapStatus(backendStatus: string): JobStatus {
  return STATUS_MAP[backendStatus] ?? 'pending';
}

function stageProgress(backendStatus: string): number {
  const stages = [
    'queued',
    'resolving',
    'extracting_metadata',
    'acquiring_transcript',
    'analyzing_segments',
    'synthesizing',
    'fact_checking',
    'complete',
  ];
  const idx = stages.indexOf(backendStatus);
  if (backendStatus === 'failed') return 0;
  if (idx === -1) return 0;
  return Math.round((idx / (stages.length - 1)) * 100);
}

// --- Transform helpers ---

function transformVisualContext(raw: ApiResultsResponse['visual_context']): VisualContext | undefined {
  if (!raw) return undefined;
  return {
    hasVideo: raw.has_video,
    speakers: raw.speakers.map((s) => ({
      label: s.label,
      bio: s.bio ?? '',
      role: s.role,
      imageUrl: s.image_url ?? null,
    })),
    keyframes: raw.keyframes.map((k) => ({
      timestampSec: k.timestamp_sec,
      description: k.description,
      speakersVisible: k.speakers_visible,
      thumbnailUrl: k.thumbnail_url,
    })),
  };
}

function transformMetadata(raw: ApiResultsResponse['metadata'], playbackUrl: string | null): EpisodeMetadata | undefined {
  if (!raw) return undefined;
  return {
    episodeTitle: raw.episode_title,
    showTitle: raw.show_title,
    publishDate: raw.publish_datetime ?? '',
    duration: raw.duration_seconds ?? 0,
    sourcePlatform: raw.source_type,
    sourceUrl: raw.source_url,
    description: raw.description,
    playbackUrl,
  };
}

function transformTranscript(segments: ApiResultsResponse['transcript_segments']): TranscriptSegment[] {
  return segments.map((s) => ({
    startTime: s.start_sec,
    endTime: s.end_sec,
    text: s.text,
    speaker: s.speaker ?? undefined,
  }));
}

function normalizeToDecimal(value: number): number {
  if (value <= 1) return value;
  if (value <= 100) return value / 100;
  return value / 10000; // handles cases like 9500
}

function transformAnalysis(
  raw: NonNullable<ApiResultsResponse['analysis']>,
  acquisitionMethod: string | null,
  analysisScope: string | null,
): AnalysisOutput {
  const topSegments: TopSegment[] = raw.top_segments.map((s, i) => ({
    rank: i + 1,
    startTime: s.start_sec,
    endTime: s.end_sec,
    snippet: s.summary,
    score: Math.round(s.relevance_score > 1 ? s.relevance_score : s.relevance_score * 100),
    reason: s.topics.join(', '),
    riskMarkers: [],
  }));

  const claimsToVerify: ClaimToVerify[] = raw.claims_to_verify.map((c) => ({
    claim: c.claim_text ?? '',
    timestamp: c.timestamp_start_sec ?? 0,
    priority: (c.priority as ClaimToVerify['priority']) ?? 'medium',
    verificationNotes: c.suggested_sources?.join('; ') ?? '',
    verification: c.verification
      ? {
          verdict: c.verification.verdict as ClaimVerification['verdict'],
          confidence: c.verification.confidence,
          evidence: c.verification.evidence,
          reasoning: c.verification.reasoning,
          sourceReferences: c.verification.source_references ?? [],
          adversarialNotes: c.verification.adversarial_notes ?? '',
          finalVerdict: c.verification.final_verdict as ClaimVerification['finalVerdict'],
        }
      : null,
  }));

  const riskFlags: RiskFlag[] = raw.risk_flags.map((r) => ({
    type: r.type ?? '',
    severity: (r.severity as RiskFlag['severity']) ?? 'medium',
    explanation: r.description ?? '',
    relatedTimestamps: [
      ...(r.timestamp_start_sec != null ? [r.timestamp_start_sec] : []),
      ...(r.timestamp_end_sec != null ? [r.timestamp_end_sec] : []),
    ],
  }));

  // Map risk flags to top segments
  raw.risk_flags.forEach((rf) => {
    rf.segment_ids.forEach((segId) => {
      const ts = topSegments.find((s) =>
        raw.top_segments.some((rs) => rs.segment_ids.includes(segId) && rs.start_sec === s.startTime),
      );
      if (ts && !ts.riskMarkers.includes(rf.type)) {
        ts.riskMarkers.push(rf.type);
      }
    });
  });

  return {
    newsworthinessScore: Math.round(raw.newsworthiness_score > 1 ? raw.newsworthiness_score : raw.newsworthiness_score * 100),
    recommendation: raw.recommendation as AnalysisOutput['recommendation'],
    confidence: normalizeToDecimal(raw.score_confidence),
    editorialSummary: raw.why_summary,
    topSegments,
    claimsToVerify,
    riskFlags,
    editorialAngles: raw.editorial_angles.map((a) => `${a.headline}: ${a.summary}`),
    analysisScope: (analysisScope ?? 'full') as AnalysisOutput['analysisScope'],
    acquisitionMethod: (acquisitionMethod ?? 'unknown') as AnalysisOutput['acquisitionMethod'],
  };
}

// --- Hooks ---

interface CreateJobResult {
  creating: boolean;
  error: string | null;
  createJob: (url: string, mode: AnalysisMode, escalateIfPromising: boolean) => Promise<{ id: string }>;
}

export function useCreateJob(): CreateJobResult {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createJob = useCallback(
    async (url: string, mode: AnalysisMode, escalateIfPromising: boolean): Promise<{ id: string }> => {
      setCreating(true);
      setError(null);

      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetch(`${API_BASE}/jobs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ url, mode, escalate_if_promising: escalateIfPromising }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || `Failed to create job: ${response.status}`);
        }

        const data = await response.json();
        return { id: data.job_id };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create job';
        setError(message);
        throw err;
      } finally {
        setCreating(false);
      }
    },
    [],
  );

  return { creating, error, createJob };
}

interface JobStatusResult {
  job: Job | null;
  loading: boolean;
  error: string | null;
}

export function useJobStatus(jobId: string | undefined): JobStatusResult {
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetch(`${API_BASE}/jobs/${jobId}`, { headers: authHeaders });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status: ${response.status}`);
        }

        const data = (await response.json()) as ApiJobStatusResponse;
        if (!cancelled) {
          const mappedJob: Job = {
            id: data.job_id,
            url: '',
            mode: 'adaptive',
            escalateIfPromising: true,
            status: mapStatus(data.status),
            progress: data.progress_pct ?? stageProgress(data.status),
            progressDetail: data.progress_detail ?? undefined,
            error: data.error ?? undefined,
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            analysis:
              data.acquisition_method || data.analysis_scope
                ? ({
                    acquisitionMethod: (data.acquisition_method ?? 'unknown') as AnalysisOutput['acquisitionMethod'],
                    analysisScope: (data.analysis_scope ?? 'full') as AnalysisOutput['analysisScope'],
                  } as Partial<AnalysisOutput> as AnalysisOutput)
                : undefined,
          };

          setJob(mappedJob);
          setLoading(false);

          if (data.status === 'complete' || data.status === 'partial' || data.status === 'failed') {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to fetch job status';
          setError(message);
          setLoading(false);
        }
      }
    };

    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 20000);

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [jobId]);

  return { job, loading, error };
}

interface JobResultsData {
  analysis: AnalysisOutput | null;
  transcript: TranscriptSegment[] | null;
  visualContext: VisualContext | null;
  job: Job | null;
}

interface JobResultsResult extends JobResultsData {
  loading: boolean;
  error: string | null;
}

export function useJobResults(jobId: string | undefined): JobResultsResult {
  const [data, setData] = useState<JobResultsData>({
    analysis: null,
    transcript: null,
    visualContext: null,
    job: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchResults = async () => {
      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetch(`${API_BASE}/jobs/${jobId}/results`, { headers: authHeaders });

        if (!response.ok) {
          throw new Error(`Failed to fetch results: ${response.status}`);
        }

        const result = (await response.json()) as ApiResultsResponse;
        if (!cancelled) {
          const metadata = transformMetadata(result.metadata, result.playback_url ?? null);

          const transcript = transformTranscript(result.transcript_segments ?? []);
          const visualContext = transformVisualContext(result.visual_context) ?? null;
          const analysis = result.analysis
            ? transformAnalysis(result.analysis, result.acquisition_method, result.analysis_scope)
            : null;

          const job: Job = {
            id: result.job_id,
            url: result.input_url,
            mode: result.mode as AnalysisMode,
            escalateIfPromising: true,
            status: mapStatus(result.status),
            metadata,
            createdAt: result.created_at,
            updatedAt: result.updated_at,
          };

          setData({ analysis, transcript, visualContext, job });
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to fetch results';
          setError(message);
          setLoading(false);
        }
      }
    };

    fetchResults();

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  return { ...data, loading, error };
}

interface JobsListResult {
  jobs: JobSummary[];
  loading: boolean;
  error: string | null;
  hasNextPage: boolean;
  nextCursor: string | null;
  refetch: () => void;
  fetchPage: (cursor?: string) => void;
}

export function useJobsList(): JobsListResult {
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);
  const fetchPage = useCallback((c?: string) => {
    setCursor(c);
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchJobs = async () => {
      try {
        const authHeaders = await getAuthHeaders();
        const url = cursor ? `${API_BASE}/jobs?startAfter=${encodeURIComponent(cursor)}` : `${API_BASE}/jobs`;
        const response = await fetch(url, { headers: authHeaders });
        if (!response.ok) throw new Error(`Failed to fetch jobs: ${response.status}`);

        const data = await response.json();
        if (!cancelled) {
          const mapped: JobSummary[] = (data.jobs ?? []).map(
            (j: {
              job_id: string;
              status: string;
              input_url: string;
              mode: string;
              created_at: string;
              completed_at: string | null;
              error: string | null;
              episode_title: string | null;
              show_title: string | null;
              newsworthiness_score: number | null;
              recommendation: string | null;
              why_summary: string | null;
            }) => ({
              jobId: j.job_id,
              status: mapStatus(j.status),
              inputUrl: j.input_url,
              mode: j.mode as AnalysisMode,
              createdAt: j.created_at,
              completedAt: j.completed_at,
              error: j.error,
              episodeTitle: j.episode_title,
              showTitle: j.show_title,
              newsworthinessScore: j.newsworthiness_score != null ? Math.round(j.newsworthiness_score) : null,
              recommendation: j.recommendation,
              whySummary: j.why_summary,
            }),
          );
          setJobs(mapped);
          setHasNextPage(data.hasNextPage ?? false);
          setNextCursor(data.nextCursor ?? null);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
          setLoading(false);
        }
      }
    };

    fetchJobs();
    return () => {
      cancelled = true;
    };
  }, [tick, cursor]);

  return { jobs, loading, error, hasNextPage, nextCursor, refetch, fetchPage };
}
